import { v4 as uuidv4 } from "uuid";
import {
  AcceptedCheckoutMethodV2,
  CheckoutMethodsData,
  Consents,
  ExistingFormData,
  Fetched,
  Field,
  FieldAnswerValue,
  Form,
  FormWithAnswers,
  FormWithIds,
  FreeCheckoutData,
  FreeCheckoutDetails,
  PurchaseForm,
  RequestError,
  Reservation,
  ReservationEventForm,
  ReservationForms,
  ReservationTicketTypeForm,
  ReservedAddonProduct,
  ReservedTicketType,
  TicketTypeForm,
} from "../types";
import fetch from "./fetch";
import * as storage from "./../helpers/storage";
import * as Sentry from "@sentry/browser";
import { getCheckoutDomain } from "../environment";

type MaxCapacityExceededForEvent = { type: "MaxCapacityExceededForEvent" };
export type MaxCapacityExceededForTicketType = {
  type: "MaxCapacityExceededForTicketType";
  ticketTypeId: number;
};

export type MaxCapacityExceededForTicketTypeGrup = {
  type: "MaxCapacityExceededForTicketTypeGroup";
  ticketTypeId: number;
};

export type NoSuchPayment = {
  type: "NoSuchPayment";
  paymentId: string;
};

type MaxCapacityExceededForAddonProduct = {
  type: "MaxAddonProductsPerEventExceeded";
  addonProductId: number;
};

export type NoSuchReservation = {
  type: "NoSuchReservation";
};

type IllegalSecretTicketReservation = {
  type: "IllegalSecretTicketReservation";
  ticketTypeId: number;
};

type CheckoutNotFound = {
  type: "CheckoutNotFound";
};

export type CouldNotAcquireLock = {
  type: "CouldNotAcquireLock";
};

export type CouldNotAcquireLockCheckout = {
  type: "CouldNotAcquireLockCheckout";
};

type UnsupportedCheckoutMethod = {
  type: "UnsupportedCheckoutMethod";
};

export type NetsUnavailable = {
  type: "NetsUnavailable";
};

export type ReservationCheckoutLocked = {
  type: "ReservationCheckoutLocked";
};

export type NoSuchCheckout = {
  type: "NoSuchCheckout";
};

export type ReservationNotFound = {
  type: "ReservationNotFound";
};

export type CouldNotAllocateTicket = { type: "CouldNotAllocateTicket"; ticketTypeId: number };
export type CouldNotAllocateAddon = { type: "CouldNotAllocateAddon"; addonProductId: number };
type GenericReservationError = { type: "GenericReservationError" };
export type CreateReservationError =
  | MaxCapacityExceededForEvent
  | MaxCapacityExceededForTicketType
  | CouldNotAllocateTicket
  | GenericReservationError
  | CouldNotAllocateAddon
  | MaxCapacityExceededForAddonProduct
  | IllegalSecretTicketReservation
  | ReservationCheckoutLocked
  | ReservationNotFound
  | MaxCapacityExceededForTicketTypeGrup;

type FormValidationError = { type: "INVALID_FORM_ANSWERS" };
type GenericFormError = { type: "GENERIC_FORM_ERROR" };
export type CreateFormError = FormValidationError | GenericFormError;

type GenericCheckoutError = { type: "GenericCheckoutError" };
export type CreateCheckoutError =
  | MaxCapacityExceededForEvent
  | MaxCapacityExceededForTicketType
  | CouldNotAllocateTicket
  | GenericReservationError
  | CouldNotAllocateAddon
  | MaxCapacityExceededForAddonProduct
  | GenericCheckoutError
  | CheckoutNotFound
  | UnsupportedCheckoutMethod
  | NoSuchPayment
  | CouldNotAcquireLock
  | CouldNotAcquireLockCheckout
  | ReservationCheckoutLocked
  | NoSuchCheckout
  | NetsUnavailable
  | MaxCapacityExceededForTicketTypeGrup;

export const createReservation = async (
  eventId: string,
  tickets: {
    ticketTypeId: number;
    addonProducts: { addon_product_id: number; comment: string }[];
  }[],
  promoCodes: string[],
  holdToken?: string,
): Promise<Fetched<{ hasForms: boolean }> | RequestError<CreateReservationError>> => {
  const response = await fetch(`/api/payment/v3.0/reservations/${eventId}`, {
    method: "POST",
    body: JSON.stringify({
      event_id: eventId,
      secret_keys: promoCodes,
      tickets: tickets.map(({ ticketTypeId, addonProducts }) => ({
        event_id: eventId,
        ticket_type_id: ticketTypeId,
        addon_product_ids: addonProducts,
      })),
      hold_token: holdToken,
    }),
  });

  if (response.type === "success") {
    const { event_forms, ticket_type_forms } = response.response;
    const hasEventForms = event_forms.some((e: any) => e.forms.length > 0);
    const hasTicketForms = ticket_type_forms.some((tt: any) => tt.forms.length > 0);

    return { status: "fetched", entity: { hasForms: hasEventForms || hasTicketForms } };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "MaxCapacityExceededForEvent") {
      return {
        status: "error",
        error: { type: "MaxCapacityExceededForEvent" },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketType") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketType",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketTypeGroup") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketTypeGroup",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "ReservationCheckoutLock") {
      return {
        status: "error",
        error: {
          type: "ReservationCheckoutLocked",
        },
      };
    } else if (response.response._error_type === "MaxAddonProductsPerEventExceeded") {
      return {
        status: "error",
        error: {
          type: "MaxAddonProductsPerEventExceeded",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAllocateTicket") {
      return {
        status: "error",
        error: { type: "CouldNotAllocateTicket", ticketTypeId: response.response.ticket_type_id },
      };
    } else if (response.response._error_type === "IllegalSecretTicketReservation") {
      return {
        status: "error",
        error: {
          type: "IllegalSecretTicketReservation",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAllocateAddon") {
      return {
        status: "error",
        error: {
          type: "CouldNotAllocateAddon",
          addonProductId: response.response.addon_product_id,
        },
      };
    }
  }
  return { status: "error", error: { type: "GenericReservationError" } };
};

type GenericRequestReservationError = { type: "Generic" };
export type RequestReservationError =
  | GenericRequestReservationError
  | ReservationCheckoutLocked
  | NoSuchReservation;

const convertForm = (form: any): Form => {
  return {
    formId: form.form_id,
    name: form.name,
    fields: form.fields.map(
      (field: any): Field => ({
        required: field.required,
        title: field.title,
        type: field.type,
        uuid: field.uuid,
        values: field.values,
        text: field.text,
      }),
    ),
    json_schema: form.json_schema,
  };
};

const existingEventFormData = (json: any): ReservationEventForm[] | null => {
  if (json.existing_form_data === null) {
    return null;
  }
  return json.existing_form_data.events.map((e: any): ReservationEventForm => {
    return {
      eventId: e.event_id,
      formWithAnswers: e.form_answers.map(
        (answers: any): FormWithAnswers => ({
          answerId: uuidv4(),
          formId: answers.form_id,
          answers: answers.answers,
          formGroupId: answers.form_group_id,
        }),
      ),
    };
  });
};

const existingTicketTypeFormData = (json: any): ReservationTicketTypeForm[] | null => {
  if (json.existing_form_data === null) {
    return null;
  }
  return json.existing_form_data.ticket_types.map((tt: any): ReservationTicketTypeForm => {
    return {
      ticketTypeId: tt.ticket_type_id,
      formWithAnswers: tt.form_answers.map(
        (answers: any): FormWithAnswers => ({
          answerId: uuidv4(),
          formId: answers.form_id,
          answers: answers.answers,
          formGroupId: answers.form_group_id,
        }),
      ),
    };
  });
};

export const repinReservation = async (params: {
  eventId: string;
}): Promise<Fetched<Date> | RequestError<RequestReservationError>> => {
  const { eventId } = params;
  const response = await fetch(`/api/payment/v3.0/reservations/${eventId}/_pin`);

  if (response.type === "success") {
    const json = response.response;
    return { status: "fetched", entity: new Date(json.last_touched) };
  } else if (response.type === "client_error") {
    if (response.response._error_type === "NoSuchReservation") {
      return {
        status: "error",
        error: { type: "NoSuchReservation" },
      };
    }
  }

  return { status: "error", error: { type: "Generic" } };
};

export const fetchReservation = async (params: {
  eventId: string;
}): Promise<Fetched<Reservation> | RequestError<RequestReservationError>> => {
  const { eventId } = params;
  const response = await fetch(`/api/payment/v3.0/reservations/${eventId}`);

  if (response.type === "success") {
    const json = response.response;

    const ticketTypeForms = json.ticket_type_forms.reduce(
      (acc: { [ticketTypeId: string]: Form[] }, ttForms: any) =>
        Object.assign(
          {
            ...acc,
          },
          {
            [ttForms.ticket_type_id]: ttForms.forms.map(convertForm),
          },
        ),
      {},
    );

    const forms: ReservationForms = {
      purchaseForms: json.event_forms.map((e: any): PurchaseForm => {
        const formGroupId = uuidv4();
        return {
          eventId: e.event_id,
          formGroupId,
          forms: e.forms.map(
            (form: any): FormWithIds => ({
              form: convertForm(form),
              answerId: uuidv4(),
              eventForm: existingEventFormData(json),
            }),
          ),
        };
      }),
      ticketTypeForms: json.tickets.map((ticket: any): TicketTypeForm => {
        const formGroupId = uuidv4();
        return {
          ticketTypeId: ticket.ticket_type_id,
          ticketTypeName: ticket.ticket_type_name,
          formGroupId,
          forms: ticketTypeForms[ticket.ticket_type_id].map(
            (form: Form): FormWithIds => ({
              form,
              answerId: uuidv4(),
              eventForm: existingTicketTypeFormData(json),
            }),
          ),
        };
      }),
    };

    const existingFormData = (json: any): ExistingFormData => {
      if (json.existing_form_data) {
        return {
          eventId: json.tickets[0].event_id,
          events: json.existing_form_data.events.map((e: any) => ({
            eventId: e.event_id,
            formWithAnswers: e.form_answers.map((fa: any) => ({
              answerId: fa.answer_id,
              formId: fa.form_id,
              formGroupId: fa.form_group_id,
              answers: fa.answers,
            })),
          })),
          ticketTypes: json.existing_form_data.ticket_types.map((tt: any) => ({
            ticketTypeId: tt.ticket_type_id,
            formWithAnswers: tt.form_answers.map((fa: any) => ({
              answerId: fa.answer_id,
              formId: fa.form_id,
              formGroupId: fa.form_group_id,
              answers: fa.answers,
            })),
          })),
        };
      } else {
        return {} as ExistingFormData;
      }
    };

    return {
      status: "fetched",
      entity: {
        reservationId: json.reservation_id,
        checkoutLock: json.checkout_lock,
        promoCodes: json.secret_keys,
        lastTouched: new Date(json.last_touched),
        tickets: json.tickets.map(
          (ticket: any): ReservedTicketType => ({
            ticketTypeId: ticket.ticket_type_id,
            ticketTypeName: ticket.ticket_type_name,
            price: ticket.price,
            fee: ticket.fee,
            vatFactor: ticket.vat_factor,
            eventId: ticket.event_id,
            addonProducts: ticket.addon_product_ids.map(
              (addon: any): ReservedAddonProduct => ({
                addonProductId: addon.addon_product_id,
                comment: addon.comment,
              }),
            ),
          }),
        ),
        forms,
        existingFormData: existingFormData(json),
      },
    };
  } else if (response.type === "client_error") {
    if (response.response._error_type === "NoSuchReservation") {
      return {
        status: "error",
        error: { type: "NoSuchReservation" },
      };
    }
  }

  return { status: "error", error: { type: "Generic" } };
};

type PurchaseFormSubmission = {
  eventId: number;
  formAnswers: {
    answerId: string;
    formGroupId: string;
    formId: number;
    answers: { [uuid: string]: FieldAnswerValue };
  }[];
};

type TicketTypeFormSubmition = {
  ticketTypeId: number;
  formAnswers: {
    answerId: string;
    formGroupId: string;
    formId: number;
    answers: { [uuid: string]: FieldAnswerValue };
  }[];
};

export const submitForms = async (
  eventId: string,
  purchaseForms: PurchaseFormSubmission[],
  ticketTypeForms: TicketTypeFormSubmition[],
): Promise<Fetched<boolean> | RequestError<CreateFormError>> => {
  const ticketTypeIdsWithForms = Array.from(
    ticketTypeForms.reduce((acc, { ticketTypeId }) => acc.add(ticketTypeId), new Set<number>()),
  );
  const ticketTypeFormSubmissions = ticketTypeIdsWithForms.map((ttId) => {
    return {
      ticket_type_id: ttId,
      form_answers: ticketTypeForms
        .filter(({ ticketTypeId }) => ticketTypeId === ttId)
        .flatMap(({ formAnswers }) =>
          formAnswers.map(({ answerId, formGroupId, formId, answers }) => ({
            answer_id: answerId,
            form_group_id: formGroupId,
            form_id: formId,
            answers,
          })),
        ),
    };
  });

  const response = await fetch(`/api/payment/v3.0/reservations/form_answers/${eventId}`, {
    method: "PUT",
    body: JSON.stringify({
      events: purchaseForms.map((pfs) => ({
        event_id: pfs.eventId,
        form_answers: pfs.formAnswers.map((fa) => ({
          answer_id: fa.answerId,
          form_group_id: fa.formGroupId,
          form_id: fa.formId,
          answers: fa.answers,
        })),
      })),
      ticket_types: ticketTypeFormSubmissions,
    }),
  });

  if (response.type === "success") {
    return {
      status: "fetched",
      entity: true,
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "invalid form answers") {
      return {
        status: "error",
        error: { type: "INVALID_FORM_ANSWERS" },
      };
    }
  }

  return {
    status: "error",
    error: { type: "GENERIC_FORM_ERROR" },
  };
};

export const checkoutMethods = async (
  eventId: string,
): Promise<Fetched<CheckoutMethodsData> | RequestError<RequestReservationError>> => {
  const response = await fetch(`/api/payment/v3.0/_available_checkout_methods/${eventId}`, {
    method: "GET",
  });

  if (response.type === "success") {
    const e = response.response;
    return {
      status: "fetched",
      entity: {
        ...e,
        reservation_last_touched: new Date(e.reservation_last_touched),
      },
    };
  } else if (response.type === "client_error") {
    if (response.response._error_type === "NoSuchReservation") {
      return {
        status: "error",
        error: { type: "NoSuchReservation" },
      };
    }
  }

  return { status: "error", error: { type: "Generic" } };
};

export const cancelCheckout = async (
  eventId: string,
): Promise<Fetched<null> | RequestError<CreateCheckoutError>> => {
  const response = await fetch(`/api/payment/v3.0/checkout/${eventId}/_cancel`, {
    method: "GET",
  });

  if (response.type === "success") {
    return {
      status: "fetched",
      entity: null,
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "CheckoutNotFound") {
      return {
        status: "error",
        error: { type: "CheckoutNotFound" },
      };
    }
  }
  return { status: "error", error: { type: "GenericCheckoutError" } };
};

export const updateReservationConsents = async (
  eventId: string,
  data: Consents,
): Promise<Fetched<null> | RequestError<CreateReservationError>> => {
  const response = await fetch(`/api/payment/v3.0/reservations/${eventId}/_consents`, {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (response.type === "success") {
    return {
      status: "fetched",
      entity: null,
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "ReservationNotFound") {
      return {
        status: "error",
        error: { type: "ReservationNotFound" },
      };
    }
  }
  return { status: "error", error: { type: "GenericReservationError" } };
};

export const updateCheckoutData = async (
  eventId: string,
  data: FreeCheckoutData,
): Promise<Fetched<FreeCheckoutData> | RequestError<CreateCheckoutError>> => {
  const response = await fetch(`/api/payment/v3.0/checkout/${eventId}/free_data`, {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (response.type === "success") {
    return {
      status: "fetched",
      entity: response.response,
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "CheckoutNotFound") {
      return {
        status: "error",
        error: { type: "CheckoutNotFound" },
      };
    }
  }
  return { status: "error", error: { type: "GenericCheckoutError" } };
};

type BusinessInvoiceAddress = {
  city: string;
  postal_code: string;
  street_address: string;
};

type BusinessInvoiceDetails = {
  company_name: string;
  purchaser_name: string;
  organization_number: string;
};

export const fetchExistingCheckout = async (
  eventId: string,
  checkoutMethod: AcceptedCheckoutMethodV2,
  existingPayment: string | null,
  existingCheckout: string | null,
  retries: number = 0,
): Promise<
  | Fetched<{
      externalReference: string;
      checkoutHash: string;
      checkoutId: string;
      checkoutMethod: AcceptedCheckoutMethodV2;
      address: BusinessInvoiceAddress | null;
      invoiceDetails: BusinessInvoiceDetails | null;
    }>
  | RequestError<CreateCheckoutError>
> => {
  var lang = storage.getItem("lang", "nb-NO");
  let response;
  if (existingCheckout) {
    response = await fetch(`/api/payment/v3.0/checkout/${eventId}/_existing_checkout`, {
      method: "POST",
      body: JSON.stringify({
        locale: lang,
        checkout_method_v2: checkoutMethod,
        existing_checkout_id: existingCheckout,
        urls: {
          two_checkout: `${window.location.protocol}//${window.location.host}/event/${eventId}/invoice`,
          complete_base: `${window.location.protocol}//${window.location.host}/complete`,
          checkout: `${window.location.protocol}//${getCheckoutDomain()}/`,
          error: `${window.location.protocol}//${window.location.host}/event/${eventId}`,
        },
      }),
    });
  } else {
    response = await fetch(`/api/payment/v3.0/checkout/${eventId}/_existing_payment`, {
      method: "POST",
      body: JSON.stringify({
        locale: lang,
        checkout_method_v2: checkoutMethod,
        existing_payment_id: existingPayment,
        urls: {
          two_checkout: `${window.location.protocol}//${window.location.host}/event/${eventId}/invoice`,
          complete_base: `${window.location.protocol}//${window.location.host}/complete`,
          checkout: `${window.location.protocol}//${getCheckoutDomain()}`,
          error: `${window.location.protocol}//${window.location.host}/event/${eventId}`,
        },
      }),
    });
  }

  if (response.type === "success") {
    const normalize_busindess_invoice_address = (address: any) => {
      if (address == null) {
        return null;
      } else {
        return {
          city: address.city,
          postal_code: address.postal_code,
          street_address: address.street_address,
        };
      }
    };
    const normalize_busindess_invoice_details = (invoice_details: any) => {
      if (invoice_details == null) {
        return null;
      } else {
        return {
          company_name: invoice_details.company_name,
          purchaser_name: `${invoice_details.first_name} ${invoice_details.last_name}`,
          organization_number: invoice_details.organization_number,
        };
      }
    };

    const address = normalize_busindess_invoice_address(response.response.address);
    const invoiceDetails = normalize_busindess_invoice_details(response.response.invoice_details);

    return {
      status: "fetched",
      entity: {
        externalReference: response.response.external_reference,
        checkoutId: response.response.checkout_id,
        checkoutHash: response.response.checkout_hash,
        checkoutMethod: response.response.checkout_method as AcceptedCheckoutMethodV2,
        address: address,
        invoiceDetails: invoiceDetails,
      },
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "MaxCapacityExceededForEvent") {
      return {
        status: "error",
        error: { type: "MaxCapacityExceededForEvent" },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketType") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketType",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketTypeGroup") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketTypeGroup",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "ReservationCheckoutLock") {
      return {
        status: "error",
        error: {
          type: "ReservationCheckoutLocked",
        },
      };
    } else if (response.response._error_type === "NoSuchCheckout") {
      return {
        status: "error",
        error: {
          type: "NoSuchCheckout",
        },
      };
    } else if (response.response._error_type === "NetsUnavailable") {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (response.response._error_type === "CouldNotAcquireLock") {
      if (retries > 0) {
        return {
          status: "error",
          error: {
            type: "CouldNotAcquireLock",
          },
        };
      } else {
        return fetchExistingCheckout(eventId, checkoutMethod, existingPayment, existingCheckout, 1);
      }
    } else if (response.response._error_type === "NoSuchPayment") {
      return {
        status: "error",
        error: {
          type: "NoSuchPayment",
          paymentId: response.response.external_reference,
        },
      };
    } else if (response.response._error_type === "MaxAddonProductsPerEventExceeded") {
      return {
        status: "error",
        error: {
          type: "MaxAddonProductsPerEventExceeded",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAllocateTicket") {
      return {
        status: "error",
        error: { type: "CouldNotAllocateTicket", ticketTypeId: response.response.ticket_type_id },
      };
    } else if (response.response._error_type === "CouldNotAllocateAddon") {
      return {
        status: "error",
        error: {
          type: "CouldNotAllocateAddon",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "UnsupportedCheckoutMethod") {
      return {
        status: "error",
        error: {
          type: "UnsupportedCheckoutMethod",
        },
      };
    }
    /* If noone above matches, we want to log it */
    Sentry.captureMessage(response.response._error_type);
  }

  if (response.type === "decoding_error") {
    Sentry.captureMessage("decoding error: " + response.rawText);
  } else if (response.type === "backend_error") {
    Sentry.captureMessage(response.type);
  }
  return { status: "error", error: { type: "GenericCheckoutError" } };
};

export const createCheckoutV2 = async (
  eventId: string,
  checkoutMethod: AcceptedCheckoutMethodV2,
  consents: Consents,
  retries: number = 0,
): Promise<
  | Fetched<{
      externalReference: string;
      checkoutHash: string;
      checkoutId: string;
      orgCoversSmsFee: boolean;
      checkoutMethod: AcceptedCheckoutMethodV2;
      checkoutDetaills: FreeCheckoutDetails[] | null;
    }>
  | RequestError<CreateCheckoutError>
> => {
  var lang = storage.getItem("lang", "nb-NO");
  const response = await fetch(`/api/payment/v3.1/checkout/${eventId}`, {
    method: "POST",
    body: JSON.stringify({
      locale: lang,
      checkout_method_v2: checkoutMethod,
      existing_payment_id: null,
      consents,
      urls: {
        two_checkout: `${window.location.protocol}//${window.location.host}/event/${eventId}/invoice`,
        complete_base: `${window.location.protocol}//${window.location.host}/complete`,
        checkout: `${window.location.protocol}//${getCheckoutDomain()}`,
        error: `${window.location.protocol}//${window.location.host}/event/${eventId}`,
      },
    }),
  });

  if (response.type === "success") {
    return {
      status: "fetched",
      entity: {
        orgCoversSmsFee: response.response.org_covers_sms_fee,
        externalReference: response.response.external_reference,
        checkoutId: response.response.checkout_id,
        checkoutHash: response.response.checkout_hash,
        checkoutMethod: response.response.checkout_method as AcceptedCheckoutMethodV2,
        checkoutDetaills: response.response.reserved_items,
      },
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "MaxCapacityExceededForEvent") {
      return {
        status: "error",
        error: { type: "MaxCapacityExceededForEvent" },
      };
    } else if (response.response._error_type === "NetsUnavailable") {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (
      response.response._error_type === "Unauthorized" &&
      response.response.message === "Unauthorized from Nets"
    ) {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (
      response.response._error_type === "InternalServerError" &&
      response.response.message === "Internal server error from Nets"
    ) {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (response.response._error_type === "CouldNotAcquireLock") {
      if (retries > 0) {
        return {
          status: "error",
          error: {
            type: "CouldNotAcquireLockCheckout",
          },
        };
      } else {
        return createCheckoutV2(eventId, checkoutMethod, consents, 1);
      }
    } else if (response.response._error_type === "MaxCapacityExceededForTicketType") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketType",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketTypeGroup") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketTypeGroup",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxAddonProductsPerEventExceeded") {
      return {
        status: "error",
        error: {
          type: "MaxAddonProductsPerEventExceeded",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAllocateTicket") {
      return {
        status: "error",
        error: { type: "CouldNotAllocateTicket", ticketTypeId: response.response.ticket_type_id },
      };
    } else if (response.response._error_type === "CouldNotAllocateAddon") {
      return {
        status: "error",
        error: {
          type: "CouldNotAllocateAddon",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "UnsupportedCheckoutMethod") {
      return {
        status: "error",
        error: {
          type: "UnsupportedCheckoutMethod",
        },
      };
    }
  }

  return { status: "error", error: { type: "GenericCheckoutError" } };
};

export const fetchCheckoutV2 = async (
  checkoutId: string,
  checkoutHash: string,
  retries: number = 0,
): Promise<
  | Fetched<{
      externalReference: string;
      checkoutHash: string;
      checkoutId: string;
      checkoutMethod: AcceptedCheckoutMethodV2;
      address: BusinessInvoiceAddress | null;
      invoiceDetails: BusinessInvoiceDetails | null;
    }>
  | RequestError<CreateCheckoutError>
> => {
  let response;
  response = await fetch(`/api/payment/v3.1/checkout/${checkoutId}/${checkoutHash}`, {
    method: "GET",
  });

  if (response.type === "success") {
    const normalize_busindess_invoice_address = (address: any) => {
      if (address == null) {
        return null;
      } else {
        return {
          city: address.city,
          postal_code: address.postal_code,
          street_address: address.street_address,
        };
      }
    };
    const normalize_busindess_invoice_details = (invoice_details: any) => {
      if (invoice_details == null) {
        return null;
      } else {
        return {
          company_name: invoice_details.company_name,
          purchaser_name: `${invoice_details.first_name} ${invoice_details.last_name}`,
          organization_number: invoice_details.organization_number,
        };
      }
    };

    const address = normalize_busindess_invoice_address(response.response.address);
    const invoiceDetails = normalize_busindess_invoice_details(response.response.invoice_details);

    return {
      status: "fetched",
      entity: {
        externalReference: response.response.external_reference,
        checkoutId: response.response.checkout_id,
        checkoutHash: response.response.checkout_hash,
        checkoutMethod: response.response.checkout_method as AcceptedCheckoutMethodV2,
        address: address,
        invoiceDetails: invoiceDetails,
      },
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "MaxCapacityExceededForEvent") {
      return {
        status: "error",
        error: { type: "MaxCapacityExceededForEvent" },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketType") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketType",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketTypeGroup") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketTypeGroup",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "ReservationCheckoutLock") {
      return {
        status: "error",
        error: {
          type: "ReservationCheckoutLocked",
        },
      };
    } else if (response.response._error_type === "NoSuchCheckout") {
      return {
        status: "error",
        error: {
          type: "NoSuchCheckout",
        },
      };
    } else if (response.response._error_type === "NetsUnavailable") {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (response.response._error_type === "CouldNotAcquireLock") {
      if (retries > 0) {
        return {
          status: "error",
          error: {
            type: "CouldNotAcquireLock",
          },
        };
      } else {
        return fetchCheckoutV2(checkoutId, checkoutHash, 1);
      }
    } else if (response.response._error_type === "NoSuchPayment") {
      return {
        status: "error",
        error: {
          type: "NoSuchPayment",
          paymentId: response.response.external_reference,
        },
      };
    } else if (response.response._error_type === "MaxAddonProductsPerEventExceeded") {
      return {
        status: "error",
        error: {
          type: "MaxAddonProductsPerEventExceeded",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAllocateTicket") {
      return {
        status: "error",
        error: { type: "CouldNotAllocateTicket", ticketTypeId: response.response.ticket_type_id },
      };
    } else if (response.response._error_type === "CouldNotAllocateAddon") {
      return {
        status: "error",
        error: {
          type: "CouldNotAllocateAddon",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "UnsupportedCheckoutMethod") {
      return {
        status: "error",
        error: {
          type: "UnsupportedCheckoutMethod",
        },
      };
    }
    /* If noone above matches, we want to log it */
    Sentry.captureMessage(response.response._error_type);
  }

  if (response.type === "decoding_error") {
    Sentry.captureMessage("decoding error: " + response.rawText);
  } else if (response.type === "backend_error") {
    Sentry.captureMessage(response.type);
  }
  return { status: "error", error: { type: "GenericCheckoutError" } };
};

export const createCheckout = async (
  eventId: string,
  checkoutMethod: AcceptedCheckoutMethodV2,
  existingPayment: string | null, // Dummy value for compatibility
  existingCheckout: string | null, // Dummy value for compatibility
  retries: number = 0,
): Promise<
  | Fetched<{
      klarnaHtml: string;
      externalReference: string;
      checkoutHash: string;
      checkoutId: string;
      orgCoversSmsFee: boolean;
      checkoutMethod: AcceptedCheckoutMethodV2;
      checkoutDetaills: FreeCheckoutDetails[] | null;
    }>
  | RequestError<CreateCheckoutError>
> => {
  var lang = storage.getItem("lang", "nb-NO");
  const response = await fetch(`/api/payment/v3.0/checkout/${eventId}`, {
    method: "POST",
    body: JSON.stringify({
      locale: lang,
      checkout_method_v2: checkoutMethod,
      existing_payment_id: null,
      urls: {
        two_checkout: `${window.location.protocol}//${window.location.host}/event/${eventId}/invoice`,
        complete_base: `${window.location.protocol}//${window.location.host}/complete`,
        checkout: `${window.location.protocol}//${getCheckoutDomain()}`,
        error: `${window.location.protocol}//${window.location.host}/event/${eventId}`,
      },
    }),
  });

  if (response.type === "success") {
    return {
      status: "fetched",
      entity: {
        orgCoversSmsFee: response.response.org_covers_sms_fee,
        externalReference: response.response.external_reference,
        klarnaHtml: response.response.html_snippet,
        checkoutId: response.response.checkout_id,
        checkoutHash: response.response.checkout_hash,
        checkoutMethod: response.response.checkout_method as AcceptedCheckoutMethodV2,
        checkoutDetaills: response.response.reserved_items,
      },
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "MaxCapacityExceededForEvent") {
      return {
        status: "error",
        error: { type: "MaxCapacityExceededForEvent" },
      };
    } else if (response.response._error_type === "NetsUnavailable") {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (
      response.response._error_type === "Unauthorized" &&
      response.response.message === "Unauthorized from Nets"
    ) {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (
      response.response._error_type === "InternalServerError" &&
      response.response.message === "Internal server error from Nets"
    ) {
      return {
        status: "error",
        error: {
          type: "NetsUnavailable",
        },
      };
    } else if (response.response._error_type === "CouldNotAcquireLock") {
      if (retries > 0) {
        return {
          status: "error",
          error: {
            type: "CouldNotAcquireLockCheckout",
          },
        };
      } else {
        return createCheckout(eventId, checkoutMethod, existingPayment, existingCheckout, 1);
      }
    } else if (response.response._error_type === "MaxCapacityExceededForTicketType") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketType",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketTypeGroup") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketTypeGroup",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxAddonProductsPerEventExceeded") {
      return {
        status: "error",
        error: {
          type: "MaxAddonProductsPerEventExceeded",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAllocateTicket") {
      return {
        status: "error",
        error: { type: "CouldNotAllocateTicket", ticketTypeId: response.response.ticket_type_id },
      };
    } else if (response.response._error_type === "CouldNotAllocateAddon") {
      return {
        status: "error",
        error: {
          type: "CouldNotAllocateAddon",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "UnsupportedCheckoutMethod") {
      return {
        status: "error",
        error: {
          type: "UnsupportedCheckoutMethod",
        },
      };
    }
  }

  return { status: "error", error: { type: "GenericCheckoutError" } };
};

export const repinCheckout = async (
  eventId: string,
  retries: number = 0,
): Promise<Fetched<boolean> | RequestError<CreateCheckoutError>> => {
  const response = await fetch(
    `/api/payment/v3.0/checkout/${eventId}`,
    {
      method: "GET",
    },
    true,
  );
  if (response.type === "success") {
    return {
      status: "fetched",
      entity: true,
    };
  }

  if (response.type === "client_error") {
    if (response.response._error_type === "MaxCapacityExceededForEvent") {
      return {
        status: "error",
        error: { type: "MaxCapacityExceededForEvent" },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketType") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketType",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "MaxCapacityExceededForTicketTypeGroup") {
      return {
        status: "error",
        error: {
          type: "MaxCapacityExceededForTicketTypeGroup",
          ticketTypeId: response.response.ticket_type_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAcquireLock") {
      if (retries > 0) {
        return {
          status: "error",
          error: {
            type: "CouldNotAcquireLock",
          },
        };
      } else {
        return repinCheckout(eventId, 1);
      }
    } else if (response.response._error_type === "MaxAddonProductsPerEventExceeded") {
      return {
        status: "error",
        error: {
          type: "MaxAddonProductsPerEventExceeded",
          addonProductId: response.response.addon_product_id,
        },
      };
    } else if (response.response._error_type === "CouldNotAllocateTicket") {
      return {
        status: "error",
        error: { type: "CouldNotAllocateTicket", ticketTypeId: response.response.ticket_type_id },
      };
    } else if (response.response._error_type === "CouldNotAllocateAddon") {
      return {
        status: "error",
        error: {
          type: "CouldNotAllocateAddon",
          addonProductId: response.response.addon_product_id,
        },
      };
    }
  }
  return { status: "error", error: { type: "GenericCheckoutError" } };
};
